import React, { useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Key, Copy, Mail, CheckCircle2, AlertTriangle, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Alert, AlertDescription } from './ui/alert';

const ClientPortalAccessGenerator = ({ open, onOpenChange, onClose, client, onAccessCreated }) => {
  const [step, setStep] = useState(1); // 1: config, 2: credentials, 3: existing access
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  
  // Configuración
  const [expirationDays, setExpirationDays] = useState('90');
  const [notes, setNotes] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  
  // Estados de envío de email
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  // Credenciales generadas
  const [accessData, setAccessData] = useState(null);
  
  // Acceso existente
  const [existingAccess, setExistingAccess] = useState(null);

  // Verificar si ya existe acceso cuando se abre el modal
  React.useEffect(() => {
    if (open && client?.has_portal_access && client?.portal_access_id) {
      loadExistingAccess();
    } else {
      setStep(1);
      setExistingAccess(null);
    }
  }, [open, client]);

  const loadExistingAccess = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/client-portal/access/${client.portal_access_id}`);
      setExistingAccess(response.data);
      setStep(3); // Mostrar paso de acceso existente
    } catch (error) {
      console.error('Error loading existing access:', error);
      toast.error('Error al cargar información del acceso');
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setEmailSending(sendEmail);
      setEmailSent(false);
      
      // Validar que haya email si se quiere enviar
      if (sendEmail && !client.email) {
        toast.error('El cliente no tiene email registrado. No se puede enviar el acceso automáticamente.');
        setLoading(false);
        setEmailSending(false);
        return;
      }
      
      // Usar el ID del cliente directamente (ya no contractor_id)
      // El email ahora viene de la tabla clients
      const requestData = {
        primary_contractor_id: client.id,
        primary_contractor_name: client.name,
        primary_contractor_email: client.email,
        aggregated_contractor_ids: [],
        expiration_days: parseInt(expirationDays),
        notes: notes || null,
        send_email: sendEmail
      };
      
      const response = await api.post('/client-portal/access', requestData);
      setAccessData(response.data);
      setStep(2);
      
      // Mensajes según si se envió email o no
      if (sendEmail) {
        toast.success('✅ Acceso generado y enviado por email al cliente');
        setEmailSent(true);
      } else {
        toast.success('Acceso generado exitosamente');
      }
    } catch (error) {
      console.error('Error generating portal access:', error);
      toast.error(error.response?.data?.detail || 'Error al generar acceso');
    } finally {
      setLoading(false);
      setEmailSending(false);
    }
  };

  const handleRegeneratePassword = async (shouldSendEmail = false) => {
    try {
      setRegenerating(true);
      setEmailSending(shouldSendEmail);
      setEmailSent(false);
      
      // Validar que haya email si se quiere enviar
      if (shouldSendEmail && !client.email && !existingAccess?.primary_contractor_email) {
        toast.error('El cliente no tiene email registrado. No se puede enviar la nueva contraseña.');
        setRegenerating(false);
        setEmailSending(false);
        return;
      }
      
      const response = await api.post(
        `/client-portal/access/${existingAccess.id}/regenerate`,
        { send_email: shouldSendEmail }
      );
      
      setAccessData(response.data);
      setStep(2);
      
      // Mensajes según si se envió email o no
      if (shouldSendEmail) {
        toast.success('✅ Contraseña regenerada y enviada por email al cliente');
        setEmailSent(true);
      } else {
        toast.success('Contraseña regenerada exitosamente');
      }
    } catch (error) {
      console.error('Error regenerating password:', error);
      toast.error(error.response?.data?.detail || 'Error al regenerar contraseña');
    } finally {
      setRegenerating(false);
      setEmailSending(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado al portapapeles`);
  };

  const handleClose = () => {
    setStep(1);
    setAccessData(null);
    setNotes('');
    setSendEmail(false);
    setEmailSent(false);
    setEmailSending(false);
    
    // Fix defensivo: verificar función antes de llamar
    if (typeof onOpenChange === 'function') {
      onOpenChange(false);
    } else if (typeof onClose === 'function') {
      onClose(); // Fallback para nombres alternativos
    }
    
    if (accessData && typeof onAccessCreated === 'function') {
      onAccessCreated();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Si la fecha es solo YYYY-MM-DD (sin hora), interpretarla como local
    // para evitar problemas de timezone
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
    
    return new Date(dateString).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        {step === 1 ? (
          <>
            {/* PASO 1: Configuración */}
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Generar Acceso al Portal de Cliente
              </DialogTitle>
              <DialogDescription>
                Crea un acceso seguro para que {client?.name} pueda ver sus pólizas
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Info del cliente */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <Label className="text-xs text-slate-500 uppercase tracking-wide">Cliente</Label>
                <p className="font-medium text-lg text-slate-900">{client?.name}</p>
                {client?.email && (
                  <p className="text-sm text-slate-600">{client?.email}</p>
                )}
                <div className="flex gap-4 mt-2 text-sm">
                  <span className="text-slate-600">
                    <strong>{client?.total_policies}</strong> pólizas
                  </span>
                  <span className="text-green-600">
                    <strong>{client?.active_policies}</strong> activas
                  </span>
                </div>
              </div>

              {/* Configuración de expiración */}
              <div>
                <Label htmlFor="expiration">Duración del Acceso</Label>
                <Select value={expirationDays} onValueChange={setExpirationDays}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 días</SelectItem>
                    <SelectItem value="90">90 días (recomendado)</SelectItem>
                    <SelectItem value="180">180 días (6 meses)</SelectItem>
                    <SelectItem value="365">365 días (1 año)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  El cliente podrá acceder al portal hasta esta fecha. Podrás renovarlo después.
                </p>
              </div>

              {/* Notas opcionales */}
              <div>
                <Label htmlFor="notes">Notas (opcional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Notas internas sobre este acceso..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Opción de envío por email */}
              <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="send_email_new"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    disabled={!client.email || loading}
                    className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <label 
                      htmlFor="send_email_new" 
                      className={`text-sm font-medium cursor-pointer ${!client.email ? 'text-slate-400' : 'text-slate-700'}`}
                    >
                      📧 Enviar credenciales por email automáticamente
                    </label>
                    <p className="text-xs text-slate-600 mt-1">
                      {client.email ? (
                        <>
                          Se enviará un email profesional con las credenciales a: <strong>{client.email}</strong>
                        </>
                      ) : (
                        <span className="text-amber-600">
                          ⚠️ Este cliente no tiene email registrado
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Advertencia de seguridad */}
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  La contraseña solo se mostrará <strong>una vez</strong>. Asegúrate de copiarla y compartirla
                  con el cliente de forma segura.
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={loading}>
                Cancelar
              </Button>
              <Button onClick={handleGenerate} disabled={loading}>
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {emailSending ? 'Generando y enviando...' : 'Generando...'}
                  </>
                ) : (
                  <>
                    <Key className="h-4 w-4 mr-2" />
                    {sendEmail ? 'Generar y Enviar por Email' : 'Generar Acceso'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        ) : step === 2 ? (
          <>
            {/* PASO 2: Credenciales Generadas */}
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="h-5 w-5" />
                ¡Acceso Generado Exitosamente!
              </DialogTitle>
              <DialogDescription>
                {emailSent ? (
                  <span className="text-green-600 font-medium">
                    ✅ Email enviado exitosamente a {client?.email || accessData?.access?.primary_contractor_email}
                  </span>
                ) : (
                  'Guarda estas credenciales y compártelas con el cliente'
                )}
              </DialogDescription>
            </DialogHeader>

            {/* Alerta de confirmación de envío */}
            {emailSent && (
              <Alert className="bg-green-50 border-green-200">
                <Mail className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  <strong>Email enviado exitosamente:</strong> El cliente recibirá un email profesional 
                  con las instrucciones de acceso, URL del portal y contraseña temporal.
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-4 py-4">
              {/* URL del Portal */}
              <div>
                <Label className="text-sm font-semibold text-slate-700">
                  🔗 URL del Portal
                </Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={accessData?.portal_url}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(accessData?.portal_url, 'URL')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Contraseña */}
              <div>
                <Label className="text-sm font-semibold text-slate-700">
                  🔑 Contraseña de Acceso
                </Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={accessData?.temp_password}
                    readOnly
                    className="font-mono text-lg font-bold text-blue-600"
                  />
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(accessData?.temp_password, 'Contraseña')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertTriangle size={12} />
                  Esta contraseña no se mostrará de nuevo. Guárdala ahora.
                </p>
              </div>

              {/* Información del acceso */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Cliente:</span>
                  <span className="font-medium">{client?.contractor_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Válido hasta:</span>
                  <span className="font-medium">{formatDate(accessData?.access.expires_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Pólizas visibles:</span>
                  <span className="font-medium">{client?.total_policies}</span>
                </div>
              </div>

              {/* Instrucciones para enviar */}
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                  📧 Mensaje para el Cliente
                </Label>
                <Textarea
                  value={accessData?.instructions}
                  readOnly
                  rows={12}
                  className="font-mono text-xs"
                />
                <div className="flex gap-2 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(accessData?.instructions, 'Instrucciones')}
                    className="flex-1"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copiar Todo
                  </Button>
                  {client?.email && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        window.location.href = `mailto:${client.email}?subject=Acceso a tu Portal de Pólizas&body=${encodeURIComponent(accessData?.instructions)}`;
                      }}
                      className="flex-1"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Enviar por Email
                    </Button>
                  )}
                </div>
              </div>

              {/* Tip */}
              {!emailSent ? (
                <Alert className="bg-blue-50 border-blue-200">
                  <ExternalLink className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    <strong>Tip:</strong> Puedes enviar estas credenciales por email, WhatsApp o el medio que prefieras.
                    El cliente podrá acceder en cualquier momento durante el período de validez.
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    <strong>Email enviado:</strong> El cliente ya recibió las credenciales por email.
                    Puedes cerrar esta ventana o copiar las credenciales para enviarlas por otro medio si lo deseas.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter>
              <Button onClick={handleClose} className="w-full">
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Entendido, Cerrar
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            {/* PASO 3: Acceso Existente */}
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="h-5 w-5 text-green-600" />
                Información del Portal de Cliente
              </DialogTitle>
              <DialogDescription>
                Este cliente ya tiene acceso configurado al portal
              </DialogDescription>
            </DialogHeader>

            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="space-y-4 py-4">
                {/* Información del acceso actual */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <h3 className="font-semibold text-green-900">Portal Activo</h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Cliente:</span>
                      <span className="font-medium">{client?.name || existingAccess?.primary_contractor_name || 'No especificado'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Email:</span>
                      <span className="font-medium">{client?.email || existingAccess?.primary_contractor_email || 'No especificado'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Creado:</span>
                      <span className="font-medium">{formatDate(existingAccess?.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Válido hasta:</span>
                      <span className="font-medium text-green-600">{formatDate(existingAccess?.expires_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Accesos realizados:</span>
                      <span className="font-medium">{existingAccess?.access_count || 0}</span>
                    </div>
                    {existingAccess?.last_access_at && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Último acceso:</span>
                        <span className="font-medium">{formatDate(existingAccess?.last_access_at)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* URL del Portal (siempre visible) */}
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                    🔗 URL del Portal
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={existingAccess ? `${window.location.origin}/portal/${existingAccess.access_token}` : ''}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      onClick={() => copyToClipboard(`${window.location.origin}/portal/${existingAccess?.access_token}`, 'URL')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => window.open(`/portal/${existingAccess?.access_token}`, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Esta URL es permanente mientras el acceso esté activo
                  </p>
                </div>

                {/* Regenerar contraseña */}
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-900">
                    <strong>Contraseña:</strong> Por seguridad, la contraseña no se puede visualizar nuevamente.
                    Si el cliente la olvidó, puedes regenerar una nueva contraseña.
                  </AlertDescription>
                </Alert>

                {/* Opciones de regeneración */}
                <div className="space-y-2">
                  <div className="grid grid-cols-1 gap-2">
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => handleRegeneratePassword(false)}
                      disabled={regenerating}
                    >
                      {regenerating ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600 mr-2"></div>
                          Regenerando...
                        </>
                      ) : (
                        <>
                          <Key className="h-4 w-4 mr-2" />
                          Regenerar contraseña
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Notas */}
                {existingAccess?.notes && (
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                      📝 Notas
                    </Label>
                    <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700">
                      {existingAccess.notes}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cerrar
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClientPortalAccessGenerator;
